import { useCallback, useEffect, useRef } from 'react';
import { useAuth, useAuthProvider } from 'src/auth/useAuth';
import { clearEntraTokenCache } from 'src/services/utils/authTokenHttp';
import { useUserInactivity } from 'src/hooks/useUserInactivity';

const PROACTIVE_REFRESH_LEAD_MS = 5 * 60 * 1000;

export function TokenAutoRefresh() {
  const auth = useAuth();
  const authProvider = useAuthProvider();
  const { shouldPauseTokenRefresh, isInactivityWarningVisible } =
    useUserInactivity();
  const refreshInFlightRef = useRef(false);

  const refreshToken = useCallback(async () => {
    if (
      refreshInFlightRef.current ||
      !auth.authenticated ||
      shouldPauseTokenRefresh ||
      isInactivityWarningVisible
    ) {
      return;
    }

    refreshInFlightRef.current = true;

    try {
      const refreshed = await auth.actions.updateToken(true);
      if (refreshed && authProvider === 'entra') {
        clearEntraTokenCache();
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [
    auth.actions,
    auth.authenticated,
    authProvider,
    isInactivityWarningVisible,
    shouldPauseTokenRefresh,
  ]);

  useEffect(() => {
    if (!auth.authenticated || !auth.tokenExpiresAt) {
      return;
    }

    const handleVisibilityCheck = () => {
      if (!auth.authenticated || !auth.tokenExpiresAt) {
        return;
      }

      if (shouldPauseTokenRefresh || isInactivityWarningVisible) {
        return;
      }

      refreshToken().catch((error) => {
        console.error('Token auto-refresh failed on visibility check:', error);
      });
    };

    window.addEventListener('focus', handleVisibilityCheck);
    document.addEventListener('visibilitychange', handleVisibilityCheck);

    const remainingMs = auth.tokenExpiresAt.getTime() - Date.now();
    const delayMs = Math.max(0, remainingMs - PROACTIVE_REFRESH_LEAD_MS);
    const timer = setTimeout(() => {
      if (shouldPauseTokenRefresh || isInactivityWarningVisible) {
        return;
      }
      refreshToken().catch((error) => {
        console.error('Token auto-refresh failed on timer:', error);
      });
    }, delayMs);

    return () => {
      window.removeEventListener('focus', handleVisibilityCheck);
      document.removeEventListener('visibilitychange', handleVisibilityCheck);
      clearTimeout(timer);
    };
  }, [
    auth.authenticated,
    auth.tokenExpiresAt,
    isInactivityWarningVisible,
    refreshToken,
    shouldPauseTokenRefresh,
  ]);

  return null;
}

export default TokenAutoRefresh;
